/**
 * CampaignQueueItemRow - Linha expansível da tabela de recipients
 * 
 * Exibe detalhes do recipient, variáveis e logs quando expandido
 */

import { TableCell, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, AlertCircle, Clock, Send, CheckCheck, Eye } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useCampaignRecipientLogs } from '@/hooks/useCampaignQueueMonitor';
import type { CampaignQueueItem } from '@/hooks/useCampaignQueueMonitor';
import { cn } from '@/lib/utils';

/**
 * Formata o tempo de processamento em formato legível
 * Calcula a diferença entre processing_started_at e sent_at (ou agora se ainda processando)
 */
function formatProcessingTime(processingStartedAt: string | null, sentAt: string | null): string {
  if (!processingStartedAt) return '-';
  
  const startTime = new Date(processingStartedAt).getTime();
  const endTime = sentAt ? new Date(sentAt).getTime() : Date.now();
  const diffMs = endTime - startTime;
  
  if (diffMs < 0) return '-';
  
  // Converter para segundos
  const diffSec = Math.floor(diffMs / 1000);
  
  if (diffSec < 60) return `${diffSec}s`;
  
  const minutes = Math.floor(diffSec / 60);
  const seconds = diffSec % 60;
  
  if (minutes < 60) return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (hours < 24) return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return `${days}d ${remainingHours}h`;
}

interface CampaignQueueItemRowProps {
  item: CampaignQueueItem;
  isExpanded: boolean;
  onToggle: () => void;
  statusColors: Record<string, string>;
  statusLabels: Record<string, string>;
}

export function CampaignQueueItemRow({ 
  item, 
  isExpanded, 
  onToggle,
  statusColors,
  statusLabels
}: CampaignQueueItemRowProps) {
  // Buscar logs quando expandido
  const { data: logs, isLoading: logsLoading } = useCampaignRecipientLogs(isExpanded ? item.id : null);

  // Formatar telefone
  const formatPhone = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 13) {
      return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 9)}-${cleaned.slice(9)}`;
    }
    if (cleaned.length === 12) {
      return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 8)}-${cleaned.slice(8)}`;
    }
    return phone;
  };

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle} asChild>
      <>
        <CollapsibleTrigger asChild>
          <TableRow className="cursor-pointer hover:bg-muted/50">
            <TableCell>
              <Button variant="ghost" size="icon" className="h-6 w-6">
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
            </TableCell>
            <TableCell>
              <Badge className={`${statusColors[item.status] || 'bg-muted'} text-white`}>
                {statusLabels[item.status] || item.status}
              </Badge>
            </TableCell>
            <TableCell className="font-mono text-sm">
              {formatPhone(item.phone)}
            </TableCell>
            <TableCell className="max-w-[150px] truncate">
              {item.contact_name || '-'}
            </TableCell>
            <TableCell className="max-w-[150px] truncate">
              {item.campaign_name}
            </TableCell>
            <TableCell className="max-w-[120px] truncate">
              {item.tenant_name}
            </TableCell>
            <TableCell>
              {item.provider_slug ? (
                <Badge variant="outline">{item.provider_slug}</Badge>
              ) : '-'}
            </TableCell>
            {/* Coluna Tempo de Processamento */}
            <TableCell className="text-sm font-mono">
              <span className={cn(
                !item.processing_started_at && 'text-muted-foreground',
                item.processing_started_at && !item.sent_at && 'text-primary animate-pulse',
                item.sent_at && 'text-foreground'
              )}>
                {formatProcessingTime(item.processing_started_at, item.sent_at)}
                {item.processing_started_at && !item.sent_at && '...'}
              </span>
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">
              {formatDistanceToNow(new Date(item.created_at), { 
                addSuffix: true, 
                locale: ptBR 
              })}
            </TableCell>
            <TableCell className="max-w-[200px]">
              {item.error_message ? (
                <span className="text-sm text-destructive truncate block">
                  {item.error_message}
                </span>
              ) : '-'}
            </TableCell>
          </TableRow>
        </CollapsibleTrigger>
        
        <CollapsibleContent asChild>
          <TableRow className="bg-muted/30">
            <TableCell colSpan={10} className="p-4">
              <div className="grid gap-4 md:grid-cols-2">
                {/* Timeline de Status */}
                <div className="space-y-3">
                  <h4 className="font-medium text-sm">Timeline</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span>Criado: {format(new Date(item.created_at), "dd/MM/yyyy HH:mm:ss", { locale: ptBR })}</span>
                    </div>
                    {item.processing_started_at && (
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-purple-500" />
                        <span>Início processamento: {format(new Date(item.processing_started_at), "dd/MM/yyyy HH:mm:ss", { locale: ptBR })}</span>
                      </div>
                    )}
                    {item.sent_at && (
                      <div className="flex items-center gap-2">
                        <Send className="h-4 w-4 text-green-500" />
                        <span>Enviado: {format(new Date(item.sent_at), "dd/MM/yyyy HH:mm:ss", { locale: ptBR })}</span>
                      </div>
                    )}
                    {item.delivered_at && (
                      <div className="flex items-center gap-2">
                        <CheckCheck className="h-4 w-4 text-emerald-500" />
                        <span>Entregue: {format(new Date(item.delivered_at), "dd/MM/yyyy HH:mm:ss", { locale: ptBR })}</span>
                      </div>
                    )}
                    {item.read_at && (
                      <div className="flex items-center gap-2">
                        <Eye className="h-4 w-4 text-teal-500" />
                        <span>Lido: {format(new Date(item.read_at), "dd/MM/yyyy HH:mm:ss", { locale: ptBR })}</span>
                      </div>
                    )}
                    {item.error_message && (
                      <div className="flex items-start gap-2 text-destructive">
                        <AlertCircle className="h-4 w-4 mt-0.5" />
                        <span className="break-all">{item.error_message}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Variáveis e Logs */}
                <div className="space-y-3">
                  {/* Variáveis */}
                  {item.variables && Object.keys(item.variables).length > 0 && (
                    <div>
                      <h4 className="font-medium text-sm mb-2">Variáveis</h4>
                      <div className="bg-background rounded-md p-2 text-xs font-mono max-h-[100px] overflow-auto">
                        <pre>{JSON.stringify(item.variables, null, 2)}</pre>
                      </div>
                    </div>
                  )}
                  
                  {/* Logs */}
                  <div>
                    <h4 className="font-medium text-sm mb-2">Logs de Execução</h4>
                    {logsLoading ? (
                      <div className="text-sm text-muted-foreground">Carregando...</div>
                    ) : logs && logs.length > 0 ? (
                      <div className="bg-background rounded-md p-2 max-h-[150px] overflow-auto space-y-1">
                        {logs.map((log: any) => (
                          <div key={log.id} className="flex items-start gap-2 text-xs">
                            <Badge 
                              variant="outline" 
                              className={
                                log.log_level === 'error' ? 'border-destructive text-destructive' :
                                log.log_level === 'warn' ? 'border-amber-500 text-amber-500' :
                                'border-muted-foreground'
                              }
                            >
                              {log.log_level}
                            </Badge>
                            <span className="text-muted-foreground">
                              {format(new Date(log.created_at), "HH:mm:ss")}
                            </span>
                            <span className="flex-1">{log.message}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground">Nenhum log encontrado</div>
                    )}
                  </div>
                </div>
              </div>
            </TableCell>
          </TableRow>
        </CollapsibleContent>
      </>
    </Collapsible>
  );
}
